import { useEffect, useId, useRef, useState, type DragEvent } from 'react';
import { CheckCircle2, FileText, Inbox, Loader2, UploadCloud, X, XCircle } from 'lucide-react';
import { EXTENSOES_ENVIO, SituacaoVinculo, type ArquivoEnviadoDto, type ImportacaoDto, type ResultadoImportacaoDto } from '@nexital/shared';
import { useDependencias, useEnvio } from '../../application/dependencias';

const ACEITOS = EXTENSOES_ENVIO.map((e) => `.${e}`).join(',');
const FORMATO = new RegExp(`\\.(${EXTENSOES_ENVIO.join('|')})$`, 'i');
const EM_ANDAMENTO = new Set([SituacaoVinculo.AguardandoLeitura, SituacaoVinculo.Agrupado]);
const mesmoArquivo = (a: File, b: File) => a.name === b.name && a.size === b.size;

/** Junta os novos arquivos aceitos aos já escolhidos, sem repetir. */
export function juntarArquivos(atuais: File[], lista: FileList | File[] | null) {
  const novos = Array.from(lista ?? []).filter((f) => FORMATO.test(f.name));
  return [...atuais, ...novos.filter((n) => !atuais.some((x) => mesmoArquivo(x, n)))];
}

/** Área de soltar e a lista dos arquivos escolhidos. */
function SelecaoArquivos({ arquivos, definir }: { arquivos: File[]; definir: (f: (a: File[]) => File[]) => void }) {
  const seletor = useRef<HTMLInputElement>(null);
  const soltar = (e: DragEvent) => {
    e.preventDefault();
    const lista = e.dataTransfer.files;
    definir((a) => juntarArquivos(a, lista));
  };
  const tirar = (arquivo: File) => definir((a) => a.filter((x) => x !== arquivo));
  return (
    <>
      <div className="nova-licitacao__area" onDragOver={(e) => e.preventDefault()} onDrop={soltar} onClick={() => seletor.current?.click()} role="button" tabIndex={0} onKeyDown={(e) => e.key === 'Enter' && seletor.current?.click()}>
        <UploadCloud size={28} aria-hidden />
        <span>Arraste os arquivos ou clique para escolher</span>
        <small>PDF, Word, planilhas, textos (TXT, HTML), imagens e ZIP</small>
      </div>
      <input
        ref={seletor}
        type="file"
        accept={ACEITOS}
        multiple
        hidden
        onChange={(e) => {
          const lista = e.target.files;
          definir((a) => juntarArquivos(a, lista));
        }}
      />
      <ul className="nova-licitacao__lista">
        {arquivos.map((a) => (
          <li key={`${a.name}-${a.size}`}>
            <FileText size={16} aria-hidden /> {a.name} <small>{(a.size / 1024 / 1024).toFixed(1)} MB</small>
            <button type="button" aria-label={`Tirar ${a.name}`} onClick={() => tirar(a)}>
              <X size={14} aria-hidden />
            </button>
          </li>
        ))}
      </ul>
    </>
  );
}

/** Acompanha o envio até cada arquivo ter destino (a cada 3 s; o aviso no sino também chega pelo tempo real). */
function useAcompanhamento(uuid: string | null) {
  const { espacos } = useDependencias();
  const [importacao, definir] = useState<ImportacaoDto | null>(null);
  useEffect(() => {
    if (!uuid) return;
    let ativo = true;
    const consultar = async () => {
      const lista = await espacos.importacoes().catch(() => []);
      if (ativo) definir(lista.find((i) => i.uuid === uuid) ?? null);
    };
    void consultar();
    const relogio = setInterval(() => void consultar(), 3000);
    return () => {
      ativo = false;
      clearInterval(relogio);
    };
  }, [uuid, espacos]);
  return importacao;
}

/**
 * Enviar arquivos (ADR-0019, ADR-0020): edital, anexos, planilhas, imagens e ZIP de um ou vários processos. O Nexital
 * lê, junta os arquivos do mesmo processo no mesmo cartão (existente ou novo) e, na dúvida, deixa na bandeja.
 */
export function DialogoEnviarArquivos({ iniciais }: { iniciais: File[] }) {
  const { espacos } = useDependencias();
  const fechar = useEnvio((s) => s.fecharDialogo);
  const ref = useRef<HTMLDialogElement>(null);
  const id = useId();
  const [arquivos, definirArquivos] = useState<File[]>(() => juntarArquivos([], iniciais));
  const [enviando, definirEnviando] = useState(false);
  const [erro, definirErro] = useState<string>();
  const [resultado, definirResultado] = useState<ResultadoImportacaoDto | null>(null);
  const importacao = useAcompanhamento(resultado?.importacao?.uuid ?? null) ?? resultado?.importacao ?? null;
  useEffect(() => {
    const d = ref.current;
    if (d && !d.open) d.showModal();
    return () => d?.close();
  }, []);

  const enviar = async () => {
    definirEnviando(true);
    definirErro(undefined);
    try {
      definirResultado(await espacos.importar(arquivos));
    } catch (e) {
      definirErro((e as Error).message);
    } finally {
      definirEnviando(false);
    }
  };
  const abrir = (processoUuid: string) => {
    espacos.navegar({ ...espacos.atual, processoUuid });
    fechar();
  };

  return (
    <dialog ref={ref} className="dialogo nova-licitacao" aria-labelledby={`${id}-titulo`} onCancel={(e) => (e.preventDefault(), fechar())}>
      <button type="button" className="painel-telegram__fechar" aria-label="Fechar" onClick={fechar}>
        <X size={18} aria-hidden />
      </button>
      <h2 id={`${id}-titulo`}>Enviar arquivos</h2>
      {!resultado ? (
        <Escolha arquivos={arquivos} definir={definirArquivos} erro={erro} enviando={enviando} enviar={() => void enviar()} fechar={fechar} />
      ) : (
        <Progresso importacao={importacao} recusados={resultado.recusados} abrir={abrir} fechar={fechar} />
      )}
    </dialog>
  );
}

type PropsEscolha = { arquivos: File[]; definir: (f: (a: File[]) => File[]) => void; erro?: string; enviando: boolean; enviar: () => void; fechar: () => void };

function Escolha({ arquivos, definir, erro, enviando, enviar, fechar }: PropsEscolha) {
  return (
    <>
      <p className="nova-licitacao__texto">
        Solte aqui o edital, os anexos, planilhas, fotos ou o ZIP do portal, de uma ou de várias licitações. O Nexital lê tudo e junta os arquivos de cada processo no cartão dele.
      </p>
      <SelecaoArquivos arquivos={arquivos} definir={definir} />
      {erro && <p className="cartao-acesso__erro" role="alert">{erro}</p>}
      <div className="dialogo__acoes">
        <button type="button" className="botao botao--secundario" onClick={fechar}>Cancelar</button>
        <button type="button" className="botao botao--primario" disabled={arquivos.length === 0 || enviando} onClick={enviar}>
          {enviando ? 'Enviando…' : `Ler ${arquivos.length === 1 ? 'o arquivo' : `os ${arquivos.length} arquivos`}`}
        </button>
      </div>
    </>
  );
}

/** Para onde foi o arquivo: lendo, cartão (novo ou existente), bandeja ou não lido. */
function Destino({ a }: { a: ArquivoEnviadoDto }) {
  if (EM_ANDAMENTO.has(a.situacao)) return <small>Lendo e identificando…</small>;
  if (a.situacao === SituacaoVinculo.Vinculado && a.processo) return <small>→ {a.processo.numero}</small>;
  if (a.situacao === SituacaoVinculo.Sugerido) return <small>Na bandeja: parece ser de {a.sugerido?.numero ?? 'outro processo'}</small>;
  if (a.situacao === SituacaoVinculo.SemIdentificacao) return <small>Na bandeja: processo não identificado</small>;
  return <small>{a.motivo ?? 'Não foi possível ler'}</small>;
}

function Icone({ a }: { a: ArquivoEnviadoDto }) {
  if (EM_ANDAMENTO.has(a.situacao)) return <Loader2 size={16} className="girando" aria-hidden />;
  if (a.situacao === SituacaoVinculo.Vinculado) return <CheckCircle2 size={16} color="#1f9d55" aria-hidden />;
  if (a.situacao === SituacaoVinculo.Sugerido || a.situacao === SituacaoVinculo.SemIdentificacao) return <Inbox size={16} color="#b7791f" aria-hidden />;
  return <XCircle size={16} color="#e5484d" aria-hidden />;
}

type PropsProgresso = { importacao: ImportacaoDto | null; recusados: ResultadoImportacaoDto['recusados']; abrir: (uuid: string) => void; fechar: () => void };

function Progresso({ importacao, recusados, abrir, fechar }: PropsProgresso) {
  const arquivos = importacao?.arquivos ?? [];
  const pronto = arquivos.length > 0 && arquivos.every((a) => !EM_ANDAMENTO.has(a.situacao));
  const processos = [...new Map(arquivos.flatMap((a) => (a.processo ? [[a.processo.uuid, a.processo] as const] : []))).values()];
  return (
    <div className="nova-licitacao__progresso" aria-live="polite">
      <ul className="nova-licitacao__lista">
        {arquivos.map((a) => (
          <li key={a.uuid}>
            <Icone a={a} /> {a.nome} <Destino a={a} />
          </li>
        ))}
        {recusados.map((r) => (
          <li key={`${r.nome}-${r.motivo}`}>
            <XCircle size={16} color="#e5484d" aria-hidden /> {r.nome} <small>{r.motivo}</small>
          </li>
        ))}
      </ul>
      <p className="nova-licitacao__texto">
        {pronto ? 'Pronto. Os arquivos que ficaram na bandeja "Arquivos sem processo" esperam você escolher o processo.' : 'Lendo e analisando os arquivos. Pode fechar: você recebe um aviso quando cada licitação estiver pronta.'}
      </p>
      <div className="dialogo__acoes">
        {processos.length === 1 ? (
          <button type="button" className="botao botao--primario" onClick={() => abrir(processos[0]!.uuid)}>
            Abrir {processos[0]!.numero}
          </button>
        ) : (
          <button type="button" className="botao botao--primario" onClick={fechar}>
            {pronto ? 'Ver no quadro' : 'Fechar'}
          </button>
        )}
      </div>
    </div>
  );
}
